// frontend/src/hooks/useSimOverlay.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  fetchSimChartData,
  toHighchartsOverlaySeries,
  applyOverlaySeries,
  removeOverlaySeries,
} from '../utils/simChartOverlay';

function getChartRange(chart) {
  const xAxis = chart?.xAxis?.[0];
  if (!xAxis) return { min: undefined, max: undefined };
  return { min: xAxis.min, max: xAxis.max };
}

export default function useSimOverlay({ apiUrl, chartRef }) {
  const [simEnabled, setSimEnabled] = useState(false);
  const [simRunId, setSimRunId] = useState('');
  const [simLoading, setSimLoading] = useState(false);

  // 옵션 토글(필요하면 UI로 노출)
  const [includeBoxlines, setIncludeBoxlines] = useState(false);
  const [includeOrders, setIncludeOrders] = useState(true);
  const [includeRisk, setIncludeRisk] = useState(false);

  // debounce
  const timerRef = useRef(null);

  // 최신 설정을 closure 이슈 없이 쓰기 위한 ref
  const settingsRef = useRef({
    apiUrl,
    simEnabled,
    simRunId,
    includeBoxlines,
    includeOrders,
    includeRisk,
  });

  useEffect(() => {
    settingsRef.current = {
      apiUrl,
      simEnabled,
      simRunId,
      includeBoxlines,
      includeOrders,
      includeRisk,
    };
  }, [
    apiUrl,
    simEnabled,
    simRunId,
    includeBoxlines,
    includeOrders,
    includeRisk,
  ]);

  const clearDebounce = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const hardRemove = useCallback(() => {
    const chart = chartRef?.current?.chart;
    if (!chart) return;
    removeOverlaySeries(chart);
  }, [chartRef]);

  const fetchAndApply = useCallback(
    async ({ min, max } = {}) => {
      const chart = chartRef?.current?.chart;
      if (!chart) return;

      const s = settingsRef.current;
      const runId = parseInt(s.simRunId, 10);

      if (!s.simEnabled || !runId) {
        removeOverlaySeries(chart);
        return;
      }

      const range = getChartRange(chart);
      const startMs = typeof min === 'number' ? min : range.min;
      const endMs = typeof max === 'number' ? max : range.max;

      // range가 아직 없으면(초기 렌더 직후) 무시
      if (typeof startMs !== 'number' || typeof endMs !== 'number') return;

      setSimLoading(true);
      try {
        const chartData = await fetchSimChartData({
          apiUrl: s.apiUrl,
          runId,
          startMs,
          endMs,
          includeBoxlines: s.includeBoxlines,
          includeOrders: s.includeOrders,
          includeRisk: s.includeRisk,
        });

        const overlaySeries = toHighchartsOverlaySeries(chartData);
        applyOverlaySeries(chart, overlaySeries);
      } catch (e) {
        // 실패 시 오버레이는 그대로 두고 로그만 남김(차트 경험 유지)
        // 필요하면 여기서 removeOverlaySeries(chart)로 강제 초기화 가능
        console.error('SIM overlay fetch/apply failed:', e);
      } finally {
        setSimLoading(false);
      }
    },
    [chartRef]
  );

  const refreshSimOverlay = useCallback(
    (range) => {
      // debounce 500ms
      clearDebounce();
      timerRef.current = setTimeout(() => {
        fetchAndApply(range);
      }, 500);
    },
    [clearDebounce, fetchAndApply]
  );

  // simEnabled/runId/options가 바뀌면 즉시 1회 적용
  useEffect(() => {
    if (!simEnabled) {
      hardRemove();
      return;
    }
    // 옵션 변경 시 즉시 갱신(초기 range는 chart에서 읽음)
    fetchAndApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simEnabled, simRunId, includeBoxlines, includeOrders, includeRisk]);

  // 언마운트 시 타이머 정리
  useEffect(() => {
    return () => clearDebounce();
  }, [clearDebounce]);

  return {
    simEnabled,
    setSimEnabled,
    simRunId,
    setSimRunId,
    simLoading,

    includeBoxlines,
    setIncludeBoxlines,
    includeOrders,
    setIncludeOrders,
    includeRisk,
    setIncludeRisk,

    refreshSimOverlay,
    removeSimOverlay: hardRemove,
  };
}
